import React, { useEffect, useState } from 'react'
import { X, Edit, Package, Users, HardDrive, Calendar, DollarSign, Clock } from 'lucide-react'
import { Modal, Button } from '@components/common'
import { apiClient } from '@api'
import './PackageView.css'

/**
 * PackageView Component
 *
 * Read-only view of package details with organized sections
 *
 * Features:
 * - Comprehensive package information display
 * - Formatted values (currency, storage, dates)
 * - Status badges
 * - Quick edit action
 * - Accessible and responsive
 * - Fetches and displays features grouped by category
 *
 * @param {Object} packageData - Package data to display
 * @param {Function} onClose - Close modal callback
 * @param {Function} onEdit - Edit action callback (optional)
 */
const PackageView = ({ packageData, onClose, onEdit }) => {
	const [featureDetails, setFeatureDetails] = useState([])
	const [loadingFeatures, setLoadingFeatures] = useState(false)
	const [featuresError, setFeaturesError] = useState(null)

	if (!packageData) return null

	/**
	 * Fetch feature details by IDs
	 */
	useEffect(() => {
		if (packageData?.features && Array.isArray(packageData.features) && packageData.features.length > 0) {
			setLoadingFeatures(true)
			setFeaturesError(null)

			apiClient
				.post('/features/batch', { ids: packageData.features })
				.then((response) => {
					setFeatureDetails(response.data || [])
				})
				.catch((error) => {
					console.error('Failed to fetch feature details:', error)
					setFeaturesError('Failed to load features')
					setFeatureDetails([])
				})
				.finally(() => {
					setLoadingFeatures(false)
				})
		} else {
			setFeatureDetails([])
		}
	}, [packageData?.features])

	/**
	 * Group features by category
	 */
	const groupFeaturesByCategory = (features) => {
		const grouped = {}
		features.forEach((feature) => {
			const category = feature.category || 'Other'
			if (!grouped[category]) {
				grouped[category] = []
			}
			grouped[category].push(feature)
		})
		return grouped
	}

	const groupedFeatures = featureDetails.length > 0 ? groupFeaturesByCategory(featureDetails) : {}

	/**
	 * Format currency
	 */
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(amount)
	}

	/**
	 * Format date
	 */
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	/**
	 * Get status badge class
	 */
	const getStatusClass = (status) => {
		return status === 'active' ? 'package-view__badge--success' : 'package-view__badge--secondary'
	}

	const footer = (
		<>
			{onEdit && (
				<Button variant="primary" onClick={onEdit}>
					<Edit size={18} />
					<span>Edit Package</span>
				</Button>
			)}
			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
		</>
	)

	return (
		<Modal isOpen={true} onClose={onClose} title="Package Details" size="large" footer={footer}>
			<div className="package-view">
				{/* Header Section */}
				<div className="package-view__header">
					<div className="package-view__header-content">
						<div className="package-view__icon">
							<Package size={32} />
						</div>
						<div>
							<h2 className="package-view__title">{packageData.title}</h2>
							<p className="package-view__slug">{packageData.slug}</p>
						</div>
					</div>
					<div className={`package-view__badge ${getStatusClass(packageData.status)}`}>
						{packageData.status}
					</div>
				</div>

				{/* Description */}
				{packageData.description && (
					<div className="package-view__section">
						<p className="package-view__description">{packageData.description}</p>
					</div>
				)}

				{/* Pricing Information */}
				<div className="package-view__section">
					<h3 className="package-view__section-title">Pricing</h3>
					<div className="package-view__grid">
						<div className="package-view__item">
							<div className="package-view__item-icon">
								<DollarSign size={20} />
							</div>
							<div className="package-view__item-content">
								<span className="package-view__item-label">Price</span>
								<span className="package-view__item-value">{formatCurrency(packageData.price)}</span>
							</div>
						</div>

						<div className="package-view__item">
							<div className="package-view__item-icon">
								<Calendar size={20} />
							</div>
							<div className="package-view__item-content">
								<span className="package-view__item-label">Billing Cycle</span>
								<span className="package-view__item-value">
									{packageData.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
								</span>
							</div>
						</div>

						<div className="package-view__item">
							<div className="package-view__item-icon">
								<Clock size={20} />
							</div>
							<div className="package-view__item-content">
								<span className="package-view__item-label">Trial Period</span>
								<span className="package-view__item-value">{packageData.trial_days} days</span>
							</div>
						</div>
					</div>
				</div>

				{/* Limits & Quotas */}
				<div className="package-view__section">
					<h3 className="package-view__section-title">Limits & Quotas</h3>
					<div className="package-view__grid">
						<div className="package-view__item">
							<div className="package-view__item-icon">
								<Users size={20} />
							</div>
							<div className="package-view__item-content">
								<span className="package-view__item-label">User Limit</span>
								<span className="package-view__item-value">
									{packageData.user_limit === -1 ? 'Unlimited' : packageData.user_limit} users
								</span>
							</div>
						</div>

						<div className="package-view__item">
							<div className="package-view__item-icon">
								<HardDrive size={20} />
							</div>
							<div className="package-view__item-content">
								<span className="package-view__item-label">Storage Limit</span>
								<span className="package-view__item-value">
									{packageData.storage_limit_formatted ||
										`${(packageData.storage_limit / (1024 * 1024 * 1024)).toFixed(0)} GB`}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Certificates */}
				{packageData.certificates && packageData.certificates.length > 0 && (
					<div className="package-view__section">
						<h3 className="package-view__section-title">
							Included Certificates ({packageData.certificates.length})
						</h3>
						<div className="package-view__certificates">
							{packageData.certificates.map((certId) => (
								<span key={certId} className="package-view__certificate-tag">
									Certificate #{certId}
								</span>
							))}
						</div>
					</div>
				)}

				{/* Features */}
				{packageData.features && Array.isArray(packageData.features) && packageData.features.length > 0 && (
					<div className="package-view__section">
						<h3 className="package-view__section-title">Features ({packageData.features.length})</h3>

						{loadingFeatures ? (
							<div className="package-view__loading">Loading features...</div>
						) : featuresError ? (
							<div className="package-view__error">{featuresError}</div>
						) : Object.keys(groupedFeatures).length > 0 ? (
							<div className="package-view__features-by-category">
								{Object.entries(groupedFeatures).map(([category, features]) => (
									<div key={category} className="package-view__category-group">
										<h4 className="package-view__category-title">
											<span
												className={`package-view__category-badge package-view__category-badge--${category.toLowerCase().replace(/\s+/g, '-')}`}
											>
												{category}
											</span>
											<span className="package-view__category-count">({features.length})</span>
										</h4>
										<div className="package-view__features">
											{features.map((feature) => (
												<div key={feature.id} className="package-view__feature">
													<span className="package-view__feature-indicator active">✓</span>
													<div className="package-view__feature-content">
														<span className="package-view__feature-name">{feature.name}</span>
														{feature.description && (
															<span className="package-view__feature-description">
																{feature.description}
															</span>
														)}
													</div>
												</div>
											))}
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="package-view__empty">No feature details available</div>
						)}
					</div>
				)}

				{/* Metadata */}
				<div className="package-view__section">
					<h3 className="package-view__section-title">Metadata</h3>
					<div className="package-view__metadata">
						<div className="package-view__metadata-item">
							<span className="package-view__metadata-label">Created</span>
							<span className="package-view__metadata-value">{formatDate(packageData.created_at)}</span>
						</div>
						<div className="package-view__metadata-item">
							<span className="package-view__metadata-label">Last Updated</span>
							<span className="package-view__metadata-value">{formatDate(packageData.updated_at)}</span>
						</div>
						{packageData.deleted_at && (
							<div className="package-view__metadata-item">
								<span className="package-view__metadata-label">Archived</span>
								<span className="package-view__metadata-value">
									{formatDate(packageData.deleted_at)}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default PackageView
